import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useRef} from 'react';
import {CSSTransition} from 'react-transition-group';

import * as S from '../../components/PageWithSidebar/PageWithSidebar.styles';

type InlineSidebarProps = {
  children: React.ReactNode;
  isOpen: boolean;
  shadow?: boolean;
  shouldAnimate?: boolean;
};

export const InlineSidebar = ({
  children,
  isOpen,
  shadow = false,
  shouldAnimate = true,
}: InlineSidebarProps) => {
  // Transitions for the outer container vs inner contents must be separate!
  // The container uses `width` transition to ensure the rest of the page
  // contents grow/shrink appropriately when the sidebar is toggled. However,
  // `width` transition on the sidebar's inner contents causes unwanted
  // compressing/truncating. Instead, we use `translateX` on the inner
  // contents to achieve the slide in/out effect.
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  return (
    <Tailwind>
      <CSSTransition
        nodeRef={outerRef}
        in={isOpen}
        timeout={shouldAnimate ? 200 : 0}
        classNames={{
          enter: 'w-0 opacity-0',
          enterActive: '!w-[300px] !opacity-100 transition-all duration-200',
          exit: 'w-[300px] opacity-100',
          exitActive: '!w-0 !opacity-0 transition-all duration-200',
        }}
        unmountOnExit>
        <div ref={outerRef} className="h-full">
          <CSSTransition
            nodeRef={innerRef}
            in={isOpen}
            timeout={200}
            classNames={{
              enter: '-translate-x-[300px]',
              enterActive: 'translate-x-0 transition-all duration-200',
              exit: 'translate-x-0',
              exitActive: '!-translate-x-[300px] transition-all duration-200',
            }}
            unmountOnExit>
            <S.InlineSidebar ref={innerRef} className="h-full" shadow={shadow}>
              {children}
            </S.InlineSidebar>
          </CSSTransition>
        </div>
      </CSSTransition>
    </Tailwind>
  );
};
